// @ts-nocheck
// The assembler setup doesn't (and cannot without a lot of changes) parse JS files and thus, doesn't know that InjectableMachineAppApplication extends another class
// This causes it to report the following error:
// - TS4112: This member cannot have an 'override' modifier because its containing class 'InjectableMachineAppApplication' does not extend another class.
// Until the assembler setup is fixed, we can't properly use Typescript here.
import { ApplicationTypes } from './constants';
import { showConfirmationModal } from '../machine_app/components/machine_app_confirmation_modal';
import { Application } from './Application';
import { gNotificationAllListener } from './Notification';
import helpers from '../helpers';
import machine_app_implementation_loader from '../machine_app/models/machine_app_implementation_loader';
import { machineAppInterface } from '../machine_app/models/machine_app_interface_singleton';
import { showMessageBox } from '../machine_app/components/machine_app_toast_message_box';
import type { Library } from './types';
import { ApplicationUI } from './ApplicationUI';

type MachineAppApplication = any;

/**
 * This extension of the regular MachineLogic Application class expresses the runtime of a MachineApp.
 * To do this, we have overidden some key methods to manage injectable behavior saving and the runtime.
 */
export class InjectableMachineAppApplication extends Application {
  mMachineAppApplication: MachineAppApplication;
  mIsMachineApp: boolean;
  mActiveApplication: any | undefined;
  mIsGenerating: boolean;

  /**
   * @param pMachineLogicLibrary MachineLogicLibrary
   * @param pMachineApp A MachineAppApplication
   * @param pInjectedAppJson JSON data of the injectable MachineApp
   * @param pAppBuilder An application builder instance in case we need to create a new JSON payload.
   */
  constructor(pMachineLogicLibrary: Library,
    pMachineApp: MachineAppApplication,
    pInjectedAppJson: object,
  ) {
    super(pMachineLogicLibrary, ApplicationTypes.MachineApp);
    Application._fromJsonInternal(this, pMachineLogicLibrary, pInjectedAppJson);
    this.mMachineAppApplication = pMachineApp;
    this.mIsMachineApp = true;
    this.mActiveApplication = undefined;
    this.mIsGenerating = false;
  }

  resetGeneratedApplication() {
    this.mLibrary.selectApplication(this);
    this.mActiveApplication = undefined;
  }

  getMachineApp() {
    return this.mMachineAppApplication;
  }

  validate() {
    this.mLibrary.getValidator().validateApp(this);
    return this.mLibrary.getValidator().mCache[this.name].length === 0;
  }

  /**
   * Generates the MachineLogic App and sets it in 'mActiveApplication'.
   * @param onGenerated Called when generation is complete
   */
  generate(onGenerated: (success: boolean) => void, onNotify: (message: string, level: 'warning' | 'error' | 'success') => void) {
    try {
      if (this.mIsGenerating) {
        onNotify('Cannot play an application while another is queued to start.', 'warning');
        onGenerated(false);
        return;
      }

      if (!this.validate()) {
        onNotify('Failed to generate app: injected behavior has errors', 'error');
        onGenerated(false);
        return;
      }

      if (!this.canRunMachineApp()) {
        onGenerated(false);
        return;
      }

      this.mIsGenerating = true;
      machine_app_implementation_loader.instance.buildApp(this.mLibrary, this.mMachineAppApplication, this.name)
        .then(pBuildableApp => {
          this.mIsGenerating = false;
          this.mActiveApplication = pBuildableApp.generate(ApplicationTypes.GeneratedMachineApp);
          if (!this.mActiveApplication) {
            throw new Error("Unable to create the active application due to a generation-time error");
          }
          onGenerated(true);
        })
        .catch(pErrors => {
          if (Array.isArray(pErrors)) {
            pErrors.forEach(pError => onNotify(pError, 'error'));
          }
          else {
            onNotify('Failed to start the application: ' + pErrors, 'error');
          }

          this.mIsGenerating = false;
          onGenerated(false);
        });
    }
    catch (e: any) {
      console.error('Failed to generate app', e);
      onNotify('An exception was thrown during MachineLogic generation: ' + e.message, 'error');
      this.mIsGenerating = false;
      onGenerated(false);
    }
  }

  /**
   * Checks whether or not we can run the MachineApp. If the answer is no, this method will also
   * fill in the UI with errors.
   *
   * @param pUiViewerContainer
   * @returns {boolean}
   */
  canRunMachineApp(pUiViewerContainer?: JQuery<HTMLElement>): boolean {
    const lValidators = machine_app_implementation_loader.instance.getValidators(this.mMachineAppApplication.getType());
    const lFlavorText = machine_app_implementation_loader.instance.getFlavorText(this.mMachineAppApplication.getType());
    const lConfigurationTitle = lFlavorText.configuration.pluralName.toLowerCase();
    const lConfigurationList = this.mMachineAppApplication.getLibrary().listConfigurations();
    const lHasConfigurations = () => lConfigurationList.length != 0;
    const lHasCalibration = () => this.mMachineAppApplication.getLibrary().getMachineParameters() != undefined;

    const lShowConfirmationModal = (pData) => {
      if (pUiViewerContainer) {
        return showConfirmationModal({
          ...pData,
          pConfirmBtnTheme: 'primary',
          pConfirmButtonStyleOverrides: { width: '240px' },
          pAppendElement: pUiViewerContainer,
          pDisableX: true
        }).css('background-color', 'transparent');
      }
      else {
        showMessageBox(pData.pTitle, 'error');
      }
    };

    const lValidateExistenceOfParameters = () => {
      //check if any configurations
      if(!lHasConfigurations()){
        lShowConfirmationModal({
          pTitle:  `No available ${lConfigurationTitle}`,
          pSubTitle: `No ${lConfigurationTitle} available. Create one to operate`,
          pConfirmText: `Create ${lConfigurationTitle}`,
          pOnConfirm: () => { machineAppInterface.get().redirectToMachineAppView(this.mMachineAppApplication.getType(), 'configuration'); }
        });
        return false;
      }
      //check if any calibrations
      else if(!lHasCalibration()){
        lShowConfirmationModal({
          pTitle: 'No available Machine Parameters',
          pSubTitle: 'No machine parameters available. Create one to operate',
          pConfirmText: 'Create Machine Parameters',
          pOnConfirm: () => { machineAppInterface.get().redirectToMachineAppView(this.mMachineAppApplication.getType(), 'machineParameters'); }
        });
        return false;
      }

      return lValidateParameters();

    };

    const lValidateParameters = () => {
      const lMachineParameterValidation = lValidators.machineParameters(
        this.mMachineAppApplication.getLibrary().getMachineParameters(),
        machineAppInterface.get());

      const getErrorList = (pErrorArray) => {
        const lErrorList = $('<ul>');
        pErrorArray.forEach(function(pError) {
          const lItem = $('<li>').appendTo(lErrorList);
          lItem.append($('<span>').text(pError));
        });
        return lErrorList;
      };

      const lValidConfigList = lConfigurationList.filter(lConfig => {
        return lValidators.configuration(lConfig).success;
      });
        //check if configurations are valid
      if(lValidConfigList.length === 0){
        lShowConfirmationModal({
          pTitle:  `Incomplete ${lConfigurationTitle}`,
          pSubTitle: `Your ${lConfigurationTitle} configuration is incomplete. Please complete in order to launch.`,
          pConfirmText: `Complete ${lConfigurationTitle}`,
          pOnConfirm: () => { machineAppInterface.get().redirectToMachineAppView(this.mMachineAppApplication.getType(), 'configuration'); }
        });
      }

      //check if calibrations are valid
      else if(!lMachineParameterValidation.success) {
        const lCalibrationErrorList = getErrorList(lMachineParameterValidation.errorList);

        lShowConfirmationModal({
          pTitle: 'Machine Parameters Incomplete',
          pSubTitle: 'Your machine parameters are incomplete. Please complete in order to launch.',
          pInformationElement: lCalibrationErrorList,
          pConfirmText: 'Complete Machine Parameters',
          pOnConfirm: () => { machineAppInterface.get().redirectToMachineAppView(this.mMachineAppApplication.getType(), 'machineParameters'); }
        });
      }

      return (lValidConfigList.length !== 0 && lMachineParameterValidation.success);
    };

    return lValidateExistenceOfParameters();
  }

  override hasUi(): boolean {
    return true;
  }

  override setName(pNewName: string) {
    this.mMachineAppApplication.setName(pNewName);
    Application.prototype.notify.call(this, 'onNameChanged', pNewName);
  }

  override deploy(onDeployed?: (json: object) => void) {
    this.mMachineAppApplication.setInjectableApplication(this.toJson(), (pSuccess) => {
      this.mDirty = false;
      if (onDeployed) onDeployed(this.toJson());
    });
  }

  override setDirty(){
    // mkosarek July 05, 2022
    // Note that we need to override the setDirty by ourselves here. The issue is that,
    // in the assembler's machinelogic.js, they set the graph dirty on each application
    // save. However, we set the app dirty ourselves via the save mechanism
    // (see src/gui/machine_app/machine_app_state.js). We dont' want to save twice,
    // so let's save it our way instead.
    helpers.debounce(() => {
      this.validate();

      if (!this.mDirty) {
        this.mDirty = true;
        this.deploy();
      }
    }, 500)();
  }

  /**
   * Override of Application.play. Instead of playing the injectable app,
   * we generate the temporary MachineLogic app and call play on that.
   * Before playing it, we also pipe all of the notifications from that application
   * through this application, in order to tell the UI about them.
   *
   * @param pRuntimeConfig
   */
  override play(pRuntimeConfig: object, callbackPlay) {
    this.generate(
      (pSuccess: boolean) => {
        if (!pSuccess) {
          this.resetGeneratedApplication();
          return;
        }

        // Note: Wire all updates from the active application through the injectable application
        // so that the injectable app can be used like a regular MachineLogic app.
        this.mActiveApplication.addListener({
          [gNotificationAllListener]: (pNotificationName, ...pArguments) => {
            this.notify(pNotificationName, ...pArguments);
          },
          onStateChanged: () => {
            if (!this.mActiveApplication?.isActive()) {
              this.resetGeneratedApplication();
            }
          }
        });
        this.mLibrary.selectApplication(this.mActiveApplication);
        this.mActiveApplication.play(pRuntimeConfig, callbackPlay);
      },
      showMessageBox
    );
  }

  /**
   * Pass-through for the running app's pause.
   */
  override pause() {
    if (this.mActiveApplication) {
      return this.mActiveApplication.pause();
    }
    else {
      console.error('Cannot pause injectable application because we are not playing.');
    }
  }

  /**
   * Pass-through for the running app's resume.
   */
  override resume() {
    if (this.mActiveApplication) {
      return this.mActiveApplication.resume();
    }
    else {
      console.error('Cannot pause injectable application because we are not playing.');
    }
  }

  /**
   * Pass-through for the running app's stop.
   */
  override stop() {
    if (this.mActiveApplication) {
      return this.mActiveApplication.stop();
    }
    else {
      console.error('Cannot pause injectable application because we are not playing.');
    }
  }

  /**
   * Pass-through for the running app's isActive.
   */
  override isActive() {
    return this.mActiveApplication !== undefined && this.mActiveApplication.isActive();
  }

  /**
   * Pass-through for the running app's isPaused.
   */
  override isPaused() {
    return this.mActiveApplication !== undefined && this.mActiveApplication.isPaused();
  }

  /**
   * Pass-through for the running app's isRunning.
   */
  override isRunning() {
    return this.mActiveApplication !== undefined && this.mActiveApplication.isRunning();
  }

  /**
   * When the frontend requests the UI, we build it on the spot. If the user has errors,
   * we build the UI with error messages for them.
   *
   * @returns {ApplicationUi}
   */
  override getUi() {
    const lUiViewerContainer: JQuery<HTMLElement> = $('<div>').css({ width: '100%', height: '100%' });

    let lBuildableOperatorUi;
    if (this.canRunMachineApp(lUiViewerContainer)) {
      lBuildableOperatorUi = machine_app_implementation_loader.instance.createBuildableOperatorUi(this.mLibrary, this.mMachineAppApplication);
      lBuildableOperatorUi.setIsReadonly(true);
      lBuildableOperatorUi.setUseFullUiSpace(true);
      return lBuildableOperatorUi.generate();
    }
    else {
      const uiBuilder = new ApplicationUI();
      const tab = uiBuilder.addTab();
      const widget = tab.tab.addWidget('custom');
      widget.setRenderFunc(function(pJqueryDiv) {
        pJqueryDiv.empty().append(lUiViewerContainer);
      });
      widget.setPosition(0, 0);
      widget.setDimensionByCellSize(8, 5);
      uiBuilder.setIsReadonly(true);
      uiBuilder.setUseFullUiSpace(true);
      return uiBuilder;
    }
  }

  /**
   * Returns the sequence of the running application if it is running, otherwise
   * the injectable application.
   * @param {string} pSequenceName
   * @returns {import('../../machinelogic/Sequence').Sequence}
   */
  override getSequence(pSequenceName: string) {
    if (this.mActiveApplication) {
      return this.mActiveApplication.getSequence(pSequenceName);
    }
    else {
      return Application.prototype.getSequence.call(this, pSequenceName);
    }
  }

  override listSequences(): string[] {
    if (this.mActiveApplication) {
      return this.mActiveApplication.listSequences();
    }
    else {
      return Application.prototype.listSequences.call(this);
    }
  }
}
